import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Snackbar,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  Visibility,
  Edit,
  Delete,
  Add,
  Receipt,
  Print,
  Person,
  Description,
  AttachMoney,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../../../components/sidebars/sidebar_reception';
import TopNav from '../../../../components/TopNav_hotelStaff';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost/Hotel/api';
const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME || 'e-Neurons Luxury Hotel';
const HOTEL_ADDRESS = process.env.REACT_APP_HOTEL_ADDRESS || 'Luxury Hotel Address';
const HOTEL_PHONE = process.env.REACT_APP_HOTEL_PHONE;

const OtherIncomeManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [incomes, setIncomes] = useState([]);
  const [filteredIncomes, setFilteredIncomes] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentStaff, setCurrentStaff] = useState(null);
  const [receiptData, setReceiptData] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
 const categories = [
  // Core Services
  'Restaurant', 'Bar', 'Laundry', 'Spa', 'Gym',
  'Meeting Room', 'Airport Transfer', 'Business Center',
  'WiFi Access',
  
  // Events
  'Banquet Hall', 'Conference Hall', 'Wedding Venue',
  
  // Recreation
  'Swimming Pool', 'Tennis Court', 
  
   'Photography', 'Floral Arrangements',
  
  // Fees & Charges
  'Service Charge', 'Late Check-out', 'Extra Bed',
  'Cancellation Fee', 'Equipment Rental',
  
  // Miscellaneous
  'Commission', 'Miscellaneous', 'Others'
];
  
  // Filter states - FIXED: Show only today's data by default
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split('T')[0], // Today's date
    endDate: new Date().toISOString().split('T')[0],   // Today's date
    category: '',
    dateRange: 'today', // Set to 'today' by default
  });
  
  // Income form states
  const [incomeForm, setIncomeForm] = useState({
    amount: '',
    paid_by: '',
    phone: '',
    description: '',
    payment_method: 'Cash',
    category: 'Other',
  });
  
  // Dialog states
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Navigation and sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Debug logging function
  const debugLog = (label, data) => {
    console.log(`[DEBUG] ${label}:`, data);
  };

  // Snackbar function
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/Entri');
  };

  const handleNotificationClick = () => {
    showSnackbar('You have new notifications!', 'info');
  };

  // Get hotel code and staff details
  const getHotelCode = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      const hotelCode = storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || 'eHT5678042024';
      debugLog('Hotel Code', hotelCode);
      return hotelCode;
    } catch (error) {
      console.error('Error getting hotel code:', error);
      return 'eHT5678042024';
    }
  };

  const getStaffId = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.id || storedProfile.staff_id || null;
    } catch (error) {
      console.error('Error getting staff ID:', error);
      return null;
    }
  };

  const getStaffName = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return `${storedProfile.first_name || ''} ${storedProfile.last_name || ''}`.trim() || storedProfile.staff_name || 'Staff';
    } catch (error) {
      console.error('Error getting staff name:', error);
      return 'Staff';
    }
  };

  const getCurrentStaff = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      setCurrentStaff(storedProfile);
      debugLog('Current Staff', storedProfile);
      return storedProfile;
    } catch (error) {
      console.error('Error getting staff details:', error);
      return null;
    }
  };

  // Helper function to extract date from MySQL datetime
  const extractDate = (datetime) => {
    if (!datetime) return '';
    // Handle both "2026-04-06 13:46:57" and "2026-04-06T13:46:57" formats
    if (datetime.includes(' ')) {
      return datetime.split(' ')[0];
    }
    if (datetime.includes('T')) {
      return datetime.split('T')[0];
    }
    return datetime;
  };

  // API service functions
  const api = {
    getIncomes: async (params = {}) => {
      try {
        const queryParams = {
          hotel_code: getHotelCode(),
          ...params
        };
        
        Object.keys(queryParams).forEach(key => {
          if (queryParams[key] === undefined || queryParams[key] === '') {
            delete queryParams[key];
          }
        });
        
        const url = `${API_BASE_URL}/hotel/staff/reception/otherincomes.php`;
        console.log('[API] GET Incomes URL:', url);
        console.log('[API] GET Incomes Params:', queryParams);
        
        const response = await axios.get(url, {
          params: queryParams,
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        console.log('[API] GET Incomes Response Status:', response.status);
        
        if (response.data && response.data.success === true) {
          const incomesArray = response.data.incomes || [];
          console.log(`[API] Successfully fetched ${incomesArray.length} income records`);
          return {
            success: true,
            incomes: incomesArray
          };
        } else {
          console.error('[API] API returned error:', response.data);
          return {
            success: false,
            incomes: [],
            error: response.data?.error || 'Unknown error'
          };
        }
      } catch (error) {
        console.error('[API] Error fetching incomes:', error);
        return { 
          success: false, 
          incomes: [],
          error: error.message 
        };
      }
    },

    getStatistics: async (params = {}) => {
      try {
        const queryParams = { 
          hotel_code: getHotelCode(),
          ...params 
        };
        
        Object.keys(queryParams).forEach(key => {
          if (queryParams[key] === undefined || queryParams[key] === '') {
            delete queryParams[key];
          }
        });
        
        const url = `${API_BASE_URL}/hotel/staff/reception/otherincomesstatistics.php`;
        console.log('[API] GET Statistics URL:', url);
        console.log('[API] GET Statistics Params:', queryParams);
        
        const response = await axios.get(url, {
          params: queryParams,
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        if (response.data && response.data.success === true) {
          return {
            success: true,
            statistics: response.data.statistics || {}
          };
        } else {
          return {
            success: false,
            statistics: {}
          };
        }
      } catch (error) {
        console.error('[API] Error fetching statistics:', error);
        return { success: false, statistics: {} };
      }
    },

    createIncome: async (incomeData) => {
      try {
        const payload = {
          ...incomeData,
          hotel_code: getHotelCode(),
          staff_id: getStaffId(),
          staff_name: getStaffName()
        };
        
        const url = `${API_BASE_URL}/hotel/staff/reception/otherincomes.php`;
        console.log('[API] POST Income URL:', url);
        console.log('[API] POST Income Payload:', payload);
        
        const response = await axios.post(url, payload, {
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        console.log('[API] POST Income Response:', response.data);
        return response.data;
      } catch (error) {
        console.error('[API] Error creating income:', error);
        return { success: false, error: 'Failed to create income record' };
      }
    },

    updateIncome: async (id, incomeData) => {
      try {
        const payload = {
          id: id,
          ...incomeData,
          hotel_code: getHotelCode()
        };
        
        const url = `${API_BASE_URL}/hotel/staff/reception/otherincomes.php`;
        console.log('[API] PUT Income URL:', url);
        console.log('[API] PUT Income Payload:', payload);
        
        const response = await axios.put(url, payload, {
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        console.log('[API] PUT Income Response:', response.data);
        return response.data;
      } catch (error) {
        console.error('[API] Error updating income:', error);
        return { success: false, error: 'Failed to update income record' };
      }
    },

    deleteIncome: async (id) => {
      try {
        const payload = { 
          id: id,
          hotel_code: getHotelCode()
        };
        
        const url = `${API_BASE_URL}/hotel/staff/reception/otherincomes.php`;
        console.log('[API] DELETE Income URL:', url);
        console.log('[API] DELETE Income Payload:', payload);
        
        const response = await axios.delete(url, {
          data: payload,
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        console.log('[API] DELETE Income Response:', response.data);
        return response.data;
      } catch (error) {
        console.error('[API] Error deleting income:', error);
        return { success: false, error: 'Failed to delete income record' };
      }
    }
  };

  // Load data function
  const loadData = async () => {
    console.log('========================================');
    console.log('[DEBUG] loadData called with filters:', filters);
    setLoading(true);
    try {
      const [incomesData, statsData] = await Promise.all([
        api.getIncomes({
          start_date: filters.startDate,
          end_date: filters.endDate,
          category: filters.category || undefined
        }),
        api.getStatistics({
          start_date: filters.startDate,
          end_date: filters.endDate
        })
      ]);

      console.log('[DEBUG] Incomes API Response Success:', incomesData.success);
      console.log('[DEBUG] Statistics API Response Success:', statsData.success);

      if (incomesData.success && incomesData.incomes) {
        console.log(`[DEBUG] Setting ${incomesData.incomes.length} income records`);
        setIncomes(incomesData.incomes);
        // Set filtered incomes directly without re-filtering
        setFilteredIncomes(incomesData.incomes);
      } else {
        console.error('[DEBUG] Failed to load incomes:', incomesData.error);
        showSnackbar(incomesData.error || 'Failed to load income records', 'error');
        setIncomes([]);
        setFilteredIncomes([]);
      }

      if (statsData.success && statsData.statistics) {
        console.log('[DEBUG] Statistics set:', statsData.statistics);
        setStatistics(statsData.statistics);
      } else {
        console.error('[DEBUG] Failed to load statistics');
        setStatistics({});
      }
    } catch (error) {
      console.error('[DEBUG] Error loading data:', error);
      showSnackbar('Failed to load data: ' + error.message, 'error');
      setIncomes([]);
      setFilteredIncomes([]);
      setStatistics({});
    } finally {
      setLoading(false);
      console.log('[DEBUG] loadData completed');
      console.log('========================================');
    }
  };

  // Apply filters to incomes - FIXED VERSION
  const applyFilters = () => {
    console.log('[DEBUG] applyFilters called');
    console.log('[DEBUG] Current incomes array length:', incomes.length);
    
    if (!incomes || incomes.length === 0) {
      console.log('[DEBUG] No incomes to filter, setting empty array');
      setFilteredIncomes([]);
      return;
    }
    
    let filtered = [...incomes];
    console.log('[DEBUG] Starting with', filtered.length, 'records');

    // FIXED: Extract date correctly using the helper function
    if (filters.startDate) {
      filtered = filtered.filter(income => {
        const incomeDate = extractDate(income.created_at);
        const result = incomeDate >= filters.startDate;
        console.log(`[DEBUG] Date check: ${incomeDate} >= ${filters.startDate} = ${result}`);
        return result;
      });
      console.log(`[DEBUG] After start date filter (${filters.startDate}): ${filtered.length} records`);
    }

    if (filters.endDate) {
      filtered = filtered.filter(income => {
        const incomeDate = extractDate(income.created_at);
        const result = incomeDate <= filters.endDate;
        console.log(`[DEBUG] Date check: ${incomeDate} <= ${filters.endDate} = ${result}`);
        return result;
      });
      console.log(`[DEBUG] After end date filter (${filters.endDate}): ${filtered.length} records`);
    }

    if (filters.category) {
      filtered = filtered.filter(income => income.category === filters.category);
      console.log(`[DEBUG] After category filter (${filters.category}): ${filtered.length} records`);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(income =>
        income.paid_by?.toLowerCase().includes(query) ||
        income.phone?.toLowerCase().includes(query) ||
        income.description?.toLowerCase().includes(query) ||
        income.income_number?.toLowerCase().includes(query)
      );
      console.log(`[DEBUG] After search filter (${searchQuery}): ${filtered.length} records`);
    }

    console.log('[DEBUG] Final filtered count:', filtered.length);
    
    setFilteredIncomes(filtered);
    setPage(0);
  };

  // Handle date range changes
  const handleDateRangeChange = (range) => {
    const today = new Date();
    console.log('[DEBUG] Date range changed to:', range);
    
    switch (range) {
      case 'today':
        setFilters(prev => ({
          ...prev,
          startDate: today.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'today'
        }));
        break;
        
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        setFilters(prev => ({
          ...prev,
          startDate: yesterday.toISOString().split('T')[0],
          endDate: yesterday.toISOString().split('T')[0],
          dateRange: 'yesterday'
        }));
        break;
        
      case 'thisWeek':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        setFilters(prev => ({
          ...prev,
          startDate: startOfWeek.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'thisWeek'
        }));
        break;
        
      case 'thisMonth':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        setFilters(prev => ({
          ...prev,
          startDate: startOfMonth.toISOString().split('T')[0],
          endDate: today.toISOString().split('T')[0],
          dateRange: 'thisMonth'
        }));
        break;
        
      case 'custom':
        setFilters(prev => ({
          ...prev,
          dateRange: 'custom'
        }));
        break;
        
      default:
        break;
    }
  };

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleDateChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value,
      dateRange: 'custom'
    }));
  };

  const clearFilters = () => {
    const today = new Date().toISOString().split('T')[0];
    console.log('[DEBUG] Clearing filters');
    setFilters({
      startDate: today,
      endDate: today,
      category: '',
      dateRange: 'today', // Set to 'today' instead of 'custom'
    });
    setSearchQuery('');
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleNewIncome = () => {
    setIncomeForm({
      amount: '',
      paid_by: '',
      phone: '',
      description: '',
      payment_method: 'Cash',
      category: 'Other',
    });
    setFormErrors({});
    setIsEditing(false);
    setIncomeDialogOpen(true);
  };

  const handleEditIncome = (income) => {
    console.log('[DEBUG] Editing income:', income);
    setSelectedIncome(income);
    setIncomeForm({
      amount: income.amount,
      paid_by: income.paid_by,
      phone: income.phone || '',
      description: income.description || '',
      payment_method: income.payment_method,
      category: income.category,
    });
    setFormErrors({});
    setIsEditing(true);
    setIncomeDialogOpen(true);
  };

  const handleDeleteIncome = (income) => {
    console.log('[DEBUG] Deleting income:', income);
    setSelectedIncome(income);
    setDeleteDialogOpen(true);
  };

  const handleViewDetails = (income) => {
    console.log('[DEBUG] Viewing income details:', income);
    setSelectedIncome(income);
    setDetailsDialogOpen(true);
  };

  const handlePrintReceipt = (income) => {
    console.log('[DEBUG] Printing receipt for income:', income);
    setReceiptData(income);
    setPrintDialogOpen(true);
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setIncomeForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!incomeForm.amount || incomeForm.amount <= 0) {
      errors.amount = 'Valid amount is required';
    }
    
    if (!incomeForm.paid_by || incomeForm.paid_by.trim() === '') {
      errors.paid_by = 'Paid by field is required';
    }
    
    if (incomeForm.phone && !/^[0-9+\-\s()]{8,20}$/.test(incomeForm.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    console.log('[DEBUG] Submitting form, isEditing:', isEditing);
    
    if (!validateForm()) {
      const firstError = Object.values(formErrors)[0];
      showSnackbar(firstError, 'error');
      return;
    }
    
    setLoading(true);
    try {
      let result;
      if (isEditing) {
        result = await api.updateIncome(selectedIncome.id, incomeForm);
        if (result.success) {
          showSnackbar('Income record updated successfully!', 'success');
          console.log('[DEBUG] Income updated successfully');
        }
      } else {
        result = await api.createIncome(incomeForm);
        if (result.success) {
          showSnackbar('Income recorded successfully!', 'success');
          console.log('[DEBUG] Income created successfully:', result.income);
          setReceiptData(result.income);
          setPrintDialogOpen(true);
        }
      }
      
      if (result.success) {
        setIncomeDialogOpen(false);
        await loadData();
      } else {
        showSnackbar(result.error || 'Operation failed', 'error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      showSnackbar('An error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    console.log('[DEBUG] Confirming delete for income ID:', selectedIncome?.id);
    setLoading(true);
    try {
      const result = await api.deleteIncome(selectedIncome.id);
      if (result.success) {
        showSnackbar('Income record deleted successfully!', 'success');
        setDeleteDialogOpen(false);
        await loadData();
      } else {
        showSnackbar(result.error || 'Failed to delete', 'error');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      showSnackbar('An error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('receipt-content').innerHTML;
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  // Calculate statistics
  const calculateStats = () => {
    console.log('[DEBUG] Calculating stats from filtered incomes:', filteredIncomes.length);
    
    let totalAmount = 0;
    let averageAmount = 0;
    let totalRecords = 0;
    
    if (statistics && Object.keys(statistics).length > 0) {
      totalAmount = parseFloat(statistics.total_amount) || 0;
      averageAmount = parseFloat(statistics.average_amount) || 0;
      totalRecords = parseInt(statistics.total_records) || 0;
      console.log('[DEBUG] Using API stats - Total:', totalAmount, 'Avg:', averageAmount, 'Records:', totalRecords);
    } else {
      totalAmount = filteredIncomes.reduce((sum, income) => sum + (income.amount || 0), 0);
      averageAmount = filteredIncomes.length > 0 ? totalAmount / filteredIncomes.length : 0;
      totalRecords = filteredIncomes.length;
      console.log('[DEBUG] Using calculated stats - Total:', totalAmount, 'Avg:', averageAmount, 'Records:', totalRecords);
    }
    
    const categoryStats = {};
    filteredIncomes.forEach(income => {
      const category = income.category || 'Other';
      if (!categoryStats[category]) {
        categoryStats[category] = { count: 0, amount: 0 };
      }
      categoryStats[category].count++;
      categoryStats[category].amount += income.amount;
    });
    
    return {
      totalAmount,
      averageAmount,
      totalRecords,
      categoryStats,
    };
  };

  const stats = calculateStats();

  // Get date range display text
  const getDateRangeText = () => {
    switch (filters.dateRange) {
      case 'today':
        return "Today's Income Records";
      case 'yesterday':
        return "Yesterday's Income Records";
      case 'thisWeek':
        return "This Week's Income Records";
      case 'thisMonth':
        return "This Month's Income Records";
      case 'custom':
        return `Income Records from ${filters.startDate} to ${filters.endDate}`;
      default:
        return "Other Income Records";
    }
  };

  // Load initial data
  useEffect(() => {
    console.log('[DEBUG] Component mounted - loading initial data');
    getCurrentStaff();
    loadData();
  }, []);

  // Reload data when date filters change
  useEffect(() => {
    console.log('[DEBUG] Filters changed - reloading data');
    if (filters.startDate && filters.endDate) {
      loadData();
    }
  }, [filters.startDate, filters.endDate, filters.category]);

  // Apply filters when incomes, filters, or search query change
  useEffect(() => {
    console.log('[DEBUG] Incomes or filters changed - applying filters');
    applyFilters();
  }, [incomes, filters, searchQuery]);

  // Handle date range changes
  useEffect(() => {
    if (filters.dateRange) {
      handleDateRangeChange(filters.dateRange);
    }
  }, [filters.dateRange]);

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={handleLogout}
          onNotificationClick={handleNotificationClick}
          toggleSidebar={toggleSidebar}
        />

        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Other Income Management
                </Typography>
                <Typography variant="subtitle1">
                  {getDateRangeText()}
                </Typography>
                {currentStaff && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    <Person sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    Staff: {currentStaff.first_name} {currentStaff.last_name || ''} ({currentStaff.user_type})
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleNewIncome}
                  sx={{ backgroundColor: 'white', color: 'primary.main', '&:hover': { backgroundColor: 'grey.100' } }}
                >
                  Record Income
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  onClick={() => setFilterDialogOpen(true)}
                  sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  Filters
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={loadData}
                  disabled={loading}
                  sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Quick Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Total Income
                      </Typography>
                      <Typography variant="h4" color="primary">
                        ₦{stats.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                    <AttachMoney sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Average Amount
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        ₦{stats.averageAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                    <Receipt sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card elevation={3}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Total Records
                      </Typography>
                      <Typography variant="h4" color="info.main">
                        {stats.totalRecords}
                      </Typography>
                    </Box>
                    <Description sx={{ fontSize: 40, color: 'info.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Category Breakdown */}
          {Object.keys(stats.categoryStats).length > 0 && (
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Income by Category
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                {Object.entries(stats.categoryStats).map(([category, data]) => (
                  <Chip
                    key={category}
                    label={`${category}: ₦${data.amount.toLocaleString()} (${data.count} records)`}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Paper>
          )}

          {/* Search Bar */}
          <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search by name, phone, description, or receipt number..."
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>

          {/* Incomes Table */}
          <Paper elevation={3}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Receipt #</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date & Time</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Paid By</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Phone</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Category</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Payment Method</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Staff</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredIncomes.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Typography variant="body1" color="textSecondary" sx={{ py: 4 }}>
                              No income records found for today
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredIncomes
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((income) => (
                            <TableRow key={income.id} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {income.income_number}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {new Date(income.created_at).toLocaleDateString()}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {new Date(income.created_at).toLocaleTimeString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {income.paid_by}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {income.phone && (
                                  <Typography variant="body2">
                                    {income.phone}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                  ₦{income.amount.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={income.category} 
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={income.payment_method} 
                                  size="small"
                                  color="success"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {income.staff_name || income.staff_id}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Tooltip title="View Details">
                                    <IconButton 
                                      size="small" 
                                      color="primary"
                                      onClick={() => handleViewDetails(income)}
                                    >
                                      <Visibility />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Print Receipt">
                                    <IconButton 
                                      size="small" 
                                      color="secondary"
                                      onClick={() => handlePrintReceipt(income)}
                                    >
                                      <Print />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredIncomes.length > 0 && (
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredIncomes.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    }}
                  />
                )}
              </>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Filter Dialog */}
      <Dialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="primary" />
            <Typography variant="h6">Filter Income Records</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={handleDateChange('startDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={handleDateChange('endDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={handleFilterChange('category')}
                  label="Category"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear All</Button>
          <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={() => setFilterDialogOpen(false)}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Income Form Dialog */}
      <Dialog 
        open={incomeDialogOpen} 
        onClose={() => setIncomeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            {isEditing ? 'Edit Income Record' : 'Record New Income'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Amount *"
                type="number"
                value={incomeForm.amount}
                onChange={handleInputChange('amount')}
                error={!!formErrors.amount}
                helperText={formErrors.amount}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={incomeForm.category}
                  onChange={handleInputChange('category')}
                  label="Category"
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Paid By *"
                value={incomeForm.paid_by}
                onChange={handleInputChange('paid_by')}
                error={!!formErrors.paid_by}
                helperText={formErrors.paid_by}
                placeholder="Name of person or company"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={incomeForm.phone}
                onChange={handleInputChange('phone')}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                placeholder="Contact phone number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={incomeForm.description}
                onChange={handleInputChange('description')}
                placeholder="Describe the income source or purpose"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={incomeForm.payment_method}
                  onChange={handleInputChange('payment_method')}
                  label="Payment Method"
                >
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  <MenuItem value="POS Terminal">POS Terminal</MenuItem>
                  <MenuItem value="Cheque">Cheque</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIncomeDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {isEditing ? 'Update' : 'Save Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Income Details</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedIncome && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Receipt Information</Typography>
                <Typography variant="body1" gutterBottom><strong>Receipt #:</strong> {selectedIncome.income_number}</Typography>
                <Typography variant="body1" gutterBottom><strong>Date:</strong> {new Date(selectedIncome.created_at).toLocaleDateString()}</Typography>
                <Typography variant="body1" gutterBottom><strong>Time:</strong> {new Date(selectedIncome.created_at).toLocaleTimeString()}</Typography>
                <Typography variant="body1" gutterBottom><strong>Category:</strong> {selectedIncome.category}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Payer Information</Typography>
                <Typography variant="body1" gutterBottom><strong>Paid By:</strong> {selectedIncome.paid_by}</Typography>
                {selectedIncome.phone && (
                  <Typography variant="body1" gutterBottom><strong>Phone:</strong> {selectedIncome.phone}</Typography>
                )}
                <Typography variant="body1" gutterBottom><strong>Payment Method:</strong> {selectedIncome.payment_method}</Typography>
                <Typography variant="body1" gutterBottom><strong>Amount:</strong> <strong style={{ color: '#4caf50' }}>₦{selectedIncome.amount.toLocaleString()}</strong></Typography>
              </Grid>
              
              {selectedIncome.description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <Typography variant="body2">{selectedIncome.description}</Typography>
                  </Paper>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Staff Information</Typography>
                <Typography variant="body1" gutterBottom><strong>Recorded By:</strong> {selectedIncome.staff_name || selectedIncome.staff_id}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<Print />}
            onClick={() => {
              setDetailsDialogOpen(false);
              handlePrintReceipt(selectedIncome);
            }}
          >
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this income record?
            {selectedIncome && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2"><strong>Receipt #:</strong> {selectedIncome.income_number}</Typography>
                <Typography variant="body2"><strong>Amount:</strong> ₦{selectedIncome.amount.toLocaleString()}</Typography>
                <Typography variant="body2"><strong>Paid By:</strong> {selectedIncome.paid_by}</Typography>
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleConfirmDelete}
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receipt Print Dialog */}
      <Dialog 
        open={printDialogOpen} 
        onClose={() => setPrintDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Print Receipt</Typography>
        </DialogTitle>
        <DialogContent>
          <Box id="receipt-content" sx={{ p: 2, fontFamily: 'monospace' }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {HOTEL_NAME}
              </Typography>
              <Typography variant="caption" display="block">
                {HOTEL_ADDRESS}
              </Typography>
              <Typography variant="caption" display="block">
                Tel: {HOTEL_PHONE}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" fontWeight="bold">
                INCOME RECEIPT
              </Typography>
              <Divider sx={{ my: 1 }} />
            </Box>

            {receiptData && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Receipt No:</strong> {receiptData.income_number}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Date:</strong> {new Date(receiptData.created_at).toLocaleString()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Category:</strong> {receiptData.category}
                  </Typography>
                </Box>

                <Divider />

                <Box sx={{ my: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Received From:</Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {receiptData.paid_by}
                  </Typography>
                  {receiptData.phone && (
                    <Typography variant="body2">
                      <strong>Phone:</strong> {receiptData.phone}
                    </Typography>
                  )}
                </Box>

                <Divider />

                <Box sx={{ my: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Payment Details:</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2"><strong>Amount:</strong></Typography>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      ₦{receiptData.amount.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2"><strong>Payment Method:</strong></Typography>
                    <Typography variant="body2">{receiptData.payment_method}</Typography>
                  </Box>
                  {receiptData.description && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2"><strong>Description:</strong></Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {receiptData.description}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider />

                <Box sx={{ my: 2 }}>
                  <Typography variant="body2">
                    <strong>Received by:</strong> {receiptData.staff_name || receiptData.staff_id}
                  </Typography>
                </Box>

                <Divider />

                <Box sx={{ my: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption">Payer's Signature</Typography>
                    <Box sx={{ mt: 4, width: 150, borderBottom: '1px solid black' }} />
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption">Hotel Stamp/Signature</Typography>
                    <Box sx={{ mt: 4, width: 150, borderBottom: '1px solid black' }} />
                  </Box>
                </Box>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="caption" display="block">
                    Thank you for your patronage!
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    This is a computer-generated receipt and requires no signature.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<Print />}
            onClick={handlePrint}
          >
            Print Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OtherIncomeManagement;